import React, { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { generatePath, useHistory, useLocation, useParams } from 'react-router-dom';
import {
  FormCard,
  YupNumeric,
  YupDate,
  YupString,
  YupBoolean,
  promiseModalDialog,
  isNumeric,
  toastSuccess,
} from 'motion-components';
import * as Yup from 'yup';
import * as Lo from 'lodash';
import { gqlGetByIdQuery, gqlAdd, gqlUpdate, gqlDelete, FormInput, gqlAccept } from '../graphql';
import '../../../../styles/tab-styles.css';
import { FormFields } from './collector-form-fields';
import { useRootStore } from 'store';
import { useHandleError } from 'shared-pages/services';
import { useTranslation } from 'react-i18next';

import { Button, Tab, Tabs } from 'react-bootstrap';
import { VehiclesGridPage } from 'shared-pages/pages/vehicles';
import { DriversGridPage } from 'shared-pages/pages/drivers';
import { RowFieldTooltips } from 'shared-pages/components';
import {
  isAfisCyprus,
  isFotokiklosi,
  isMedicycle,
  isRebattery,
  requiredString,
  regionsTitle,
  isRebatteryLiion,
  isCombatt,
} from 'shared-pages/common/constants';
import { CollectorsNomoiGridPage } from 'shared-pages/pages/collectors/form/nomoi-tab/collectors-nomoi-grid-page';

import { SeriesGridPage } from 'shared-pages/pages/series';
import { getRouteByName } from 'app/routes';
import { formatString } from 'shared-pages/common/string-helpfunc';
import { CollectorsMaterialsGridPage } from './materials-tab/collectors-materials-grid-page';
import { useFormPermissions } from './form-permissions';
import { CollectorsRegionsGridPage } from './regions-tab/collectors-regions-grid-page';
import { DummyCollectorId } from 'shared-pages/shared/constants';

// Customize FormShape based on Customer/Company
function useCustomFormShape(base: any) {
  const { t } = useTranslation();
  const reqStr = t(requiredString);

  let formShape = Yup.object(base);

  // // Use concat to overlay stricter rules on top of the existing object schema.
  // formShape = formShape.concat(
  //   Yup.object({
  //     adrNumber: Yup.string().required(reqStr),
  //     adrExpDate: YupDate.required(reqStr),
  //   }),
  // );

  // // Update a field with shape (also immutable)
  // formShape = formShape.shape({
  //   name: Yup.string().notRequired(),
  //   vehicleId: formShape.fields['vehicleId'].required('Vehicle is required'),
  // });

  // Medicycle
  if (isAfisCyprus || isMedicycle) {
    // Update a field with shape (also immutable)
    formShape = formShape.shape({
      companyVat: YupString.notRequired(),
      companyTaxOffice: YupString.notRequired(),
    });
  }

  return formShape;
}

/**
 *  Form Page
 */
export const CollectorFormPage = () => {
  const { handleGraphqlError } = useHandleError('collector');
  const { appStore } = useRootStore();
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const idNum = isNumeric(id) ? parseInt(id) : -1;
  const isNew = idNum < 0;
  const { t } = useTranslation();

  const formShape = {
    id: YupNumeric,
    legacyAppId: YupString,
    name: YupString.required(),
    companyVat: YupString.required(),
    companyTaxOffice: !isAfisCyprus ? YupString.required() : YupString,
    imaCode: YupString,
    imaCode2: YupString,
    contractNo: YupString,
    contractSignDate: YupDate,
    contractExpDate: YupDate,
    formalName: YupString,
    addressLine1: YupString.required(),
    addressLine2: YupString,
    addressArea: YupString,
    addressCity: YupString,
    addressNomos: YupString,
    postalCode: YupString,
    regionId: YupNumeric,
    subregionId: YupNumeric,
    phone: YupString,
    mobile: YupString,
    fax: YupString,
    email: YupString,
    email2: YupString,
    website: YupString,
    contactperson: YupString,
    notes: YupString,
    hasGps: YupBoolean,
    insurancePolicyNumber: YupString,
    insuranceExpirationDate: YupDate,
    hazardousWastePermit: YupString,
    hazardousWasteExpDate: YupDate,
    environmentalPermit: YupString,
    envPermitExpirationDate: YupDate,
    adrConsultantAgrExpDate: YupDate,
    sameePermitExpDate: YupDate,
    recordDate: YupDate,
    signatureFileId: YupNumeric,
    stampFileId: YupNumeric,
    insuranceFileId: YupNumeric,
    hazardousWastePermitFileId: YupNumeric,
    adrFileId: YupNumeric,
    proofColPermitRenewalFileId: YupNumeric,
    sameePermitFileId: YupNumeric,
    isActive: YupBoolean,
    isApproved: YupBoolean,
    approvedBy: YupString,
    approvedDate: YupDate,
    edocSeries: Yup.array().of(
      Yup.object().shape({
        id: YupNumeric,
        docPrefix: Yup.string(),
      }),
    ),
  };

  const newRecord = useMemo<FormValuesType>(
    () =>
      ({
        id: -1,
        isActive: true,
      } as FormValuesType),
    [],
  );

  // const validationSchema = Yup.object().shape(formShape);
  const validationSchema = useCustomFormShape(formShape);
  const pickProperties = (values) => Lo.pick(values, Lo.keys(formShape));
  type FormValuesType = Yup.InferType<typeof validationSchema>;

  const [addRecord] = useMutation(gqlAdd);
  const [updateRecord] = useMutation(gqlUpdate);
  const [deleteRecord] = useMutation(gqlDelete);
  const [accept] = useMutation(gqlAccept);
  const [get, { data: rawData, error, refetch }] = useLazyQuery(gqlGetByIdQuery);
  const data = isNew ? newRecord : rawData ? rawData[Object.keys(rawData)[0]] : undefined;

  /* user Permissions */
  const { canView, canViewReasoning } = useFormPermissions(data);

  const tabStyle = { minHeight: '360px' };
  const defaultTab = () => {
    if (isRebattery) return 'regions';
    else if (isFotokiklosi) return 'nomoi';
    else if (isAfisCyprus) return 'regions';
    else return 'drivers';
  };
  const [key, setKey] = useState(defaultTab());
  const regionsGridRefetchRef = useRef<(() => void) | null>(null);

  /* fetch data */
  useEffect(() => {
    get({ variables: { id: idNum } });
  }, [get, id, idNum]);

  // Handler να πάει στην πίσω σελίδα
  const handleReturn = useCallback(() => {
    history.push(generatePath(getRouteByName('Collectors')), { ...(location as any).state });
  }, [history, location.state]);

  /**
   * * APPROVE
   */
  const handleApprove = async () => {
    accept({ variables: { id: idNum } })
      .then((d) => {
        toastSuccess(`${t('Η εγγραφή εγκρίθηκε!')} [${d.data.acceptCollector}]`);
        refetch();
      })
      .catch((error) => {
        handleGraphqlError('mutation-accept', error);
      });
  };

  /**
   * * ΑΠΟΘΗΚΕΥΣΗ - UPDATE/CREATE
   */
  const handleSubmit = (values: FormValuesType, actions) => {
    actions.setSubmitting(false);
    const mutValues = Lo.pick(values, Lo.keys(new FormInput()));
    // Αν είναι update εγγραφής τοτε γράφω μονο τα αλλαγμένα πεδία
    console.log('[Applog] Update:', mutValues);
    if (isNew) {
      console.log('[User] New:', values);
      addRecord({ variables: { data: { ...values } } })
        .then((d) => {
          toastSuccess(`${t('Επιτυχής Προσθήκη Εγγραφής')} [${d.data[Object.keys(d.data)[0]].id}]`);
          handleReturn();
        })
        .catch((error) => {
          handleGraphqlError('mutation-add', error);
        });
    } else {
      updateRecord({ variables: { data: { ...mutValues, id: idNum } } })
        .then((d) => {
          toastSuccess(
            formatString(t('Επιτυχής Ενημέρωση Εγγραφής [{0}]'), d.data[Object.keys(d.data)[0]].id),
          );
          //toastSuccess(`Επιτυχής Ενημέρωση Εγγραφής [${d.data[Object.keys(d.data)[0]].id}]`);
          refetch();
        })
        .catch((error) => {
          handleGraphqlError('mutation-update', error);
        });
    }
  };

  /**
   * *  DELETE
   */
  const handleDelete = useCallback(async () => {
    const res = await promiseModalDialog({
      title: 'Διαγραφή Εγγραφής',
      text: 'Είστε σίγουροι ότι θέλετε να διαγράψετε την Εγγραφή;',
    });
    if (res) {
      deleteRecord({ variables: { id: idNum } })
        .then(() => history.goBack())
        .catch((error) => {
          handleGraphqlError('mutation-delete', error);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteRecord, idNum]);

  const [initCollectorRegions] = useMutation(gql`
    mutation initCollectorRegions($collectorId: Int) {
      initCollectorRegions(collectorId: $collectorId)
    }
  `);
  async function initRegionsHandler(event: any): Promise<void> {
    const res = await initCollectorRegions({
      variables: { collectorId: data.id },
    }).catch((reason) => {
      console.error('initCollectorRegions', reason);
      handleGraphqlError('mutation-initCollectorRegions-failed', reason);
    });
    toastSuccess(`${t('Επιτυχής Αρχικοποίηση Περιφερειών του Συλλέκτη')} [${data.name}]`);
    regionsGridRefetchRef.current?.();
  }

  /**
   * * RENDERING
   */
  if (error) {
    handleGraphqlError('query-get', error);
    return <h3 className="text-center">{error.message}</h3>;
  } else if (canView === false) {
    return <h3 className="text-center">{canViewReasoning}</h3>;
  } else {
    return (
      <>
        <FormCard
          title={`${t('Συλλέκτης')}: ${!isNew ? data?.name : t('Νέος Συλλέκτης')}`}
          onSubmit={handleSubmit}
          onDelete={handleDelete}
          onReturn={handleReturn}
          permitSubmit={false}
          permitDelete={data?.id !== DummyCollectorId && appStore.isPlatformOwner}
          initialValues={pickProperties(data)}
          validationSchema={validationSchema}
          enableReinitialize
          idx={idNum}
        >
          <FormFields
            isNew={isNew}
            updateRecord={updateRecord}
            refetch={refetch}
            data={data}
            handleApprove={handleApprove}
          />
          {appStore.isPlatformOwner && <RowFieldTooltips data={data} />}
        </FormCard>
        {data && data.id > 0 && (
          <div>
            <Tabs activeKey={key} onSelect={(k) => setKey(k)}>
              {(isRebattery || isRebatteryLiion || isAfisCyprus || isMedicycle) && (
                <Tab eventKey="regions" title={t(regionsTitle)} style={tabStyle}>
                  {key == 'regions' && (
                    <CollectorsRegionsGridPage
                      collectorId={data.id}
                      isGridInForm={true}
                      onRefetchReady={(gridRefetch) => {
                        regionsGridRefetchRef.current = gridRefetch;
                      }}
                      injectFooterElement={
                        <Button size="sm" onClick={initRegionsHandler}>
                          Αρχικοποίηση Περιφερειών
                        </Button>
                      }
                    />
                  )}
                </Tab>
              )}
              {(isFotokiklosi || isCombatt) && (
                <Tab eventKey="nomoi" title={t('Νομοί')} style={tabStyle}>
                  {key == 'nomoi' && (
                    <CollectorsNomoiGridPage collectorId={data.id} isGridInForm={true} />
                  )}
                </Tab>
              )}
              {isFotokiklosi && (
                <Tab eventKey="materials" title={t('Απόβλητα')} style={tabStyle}>
                  {key == 'materials' && (
                    <CollectorsMaterialsGridPage collectorId={data.id} isGridInForm={true} />
                  )}
                </Tab>
              )}
              <Tab eventKey="drivers" title={t('Οδηγοί')} style={tabStyle}>
                {key == 'drivers' && (
                  <DriversGridPage collectorId={data.id} isGridInForm={true}></DriversGridPage>
                )}
              </Tab>
              <Tab eventKey="vehicles" title={t('Οχήματα')} style={tabStyle}>
                {key == 'vehicles' && (
                  <VehiclesGridPage collectorId={data.id} isGridInForm={true} />
                )}
              </Tab>

              {!appStore.isCollectionPoint && (
                <Tab eventKey="col-series" title={t('Σειρές Αρίθμησης')} style={tabStyle}>
                  {key == 'col-series' && (
                    <SeriesGridPage collectorId={data.id} isGridInForm={true} />
                  )}
                </Tab>
              )}
            </Tabs>
          </div>
        )}
      </>
    );
  }
};
